import { CandidateRating } from '../../types';
import {
  useCareers, useCreateCareer, useUpdateCareer, useDeleteCareer
} from '../../hooks/useCareers';
import {
  useCandidates, useCreateCandidate, useUpdateCandidate, useDeleteCandidate
} from '../../hooks/useCandidates';
import { useAssessmentPoints } from '../../hooks/useAssessmentPoints';
import { uploadFiles } from '../../services/upload.service';
import { Job, Application, convertEmploymentTypeFromAPI, convertEmploymentTypeToAPI } from './types';

/**
 * salaryRange is a JSON column: values written by the form arrive as a
 * JSON-encoded string ("\"Negotiable\""), while older rows may hold raw
 * numbers or objects. Decode back to display text.
 */
function decodeSalaryRange(value: any): string {
  if (value === null || value === undefined) return 'Negotiable';
  if (typeof value === 'number') return String(value);
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return typeof parsed === 'string' ? parsed : String(parsed);
    } catch {
      return value;
    }
  }
  return 'Negotiable';
}

export function useHiringData() {
  const { data: apiCareers = [], refetch: refetchCareers } = useCareers();
  // Mutations must refetch the collection — the hook caches rows in local
  // state, so without this the list/edit modal kept showing pre-save values
  // (e.g. the form-based checkbox appearing unsaved).
  const createCareerMutation = useCreateCareer(refetchCareers);
  const updateCareerMutation = useUpdateCareer(refetchCareers);
  const deleteCareerMutation = useDeleteCareer(refetchCareers);

  const { data: apiCandidates = [], refetch: refetchCandidates } = useCandidates();
  const createCandidateMutation = useCreateCandidate(refetchCandidates);
  const updateCandidateMutation = useUpdateCandidate(refetchCandidates);
  const deleteCandidateMutation = useDeleteCandidate(refetchCandidates);

  const { data: assessmentPoints = [] } = useAssessmentPoints();

  // Applicants per job title — real job applications only; seafarer-form
  // submissions live in the Candidates module and are not counted here.
  const applicantCounts = new Map<string, number>();
  apiCandidates.forEach((c: any) => {
    if (!c.appliedPosition || c.source === 'website_form') return;
    applicantCounts.set(c.appliedPosition, (applicantCounts.get(c.appliedPosition) || 0) + 1);
  });

  const jobs: Job[] = apiCareers.map((career) => ({
    id: career.documentId || career.id,
    title: career.title,
    department: career.department,
    location: career.location,
    type: convertEmploymentTypeFromAPI(career.employmentType),
    salary: decodeSalaryRange(career.salaryRange),
    postedDate: career.createdAt?.split('T')[0] || '',
    deadline: career.applicationDeadline || '',
    status: career.isActive ? 'Open' : 'Closed',
    applicants: applicantCounts.get(career.title) || 0,
    description: career.description,
    requirements: career.requirements ? (typeof career.requirements === 'string' ? JSON.parse(career.requirements) : career.requirements) : [],
    useSeafarerForm: !!career.useSeafarerForm,
    documentId: career.documentId
  } as any));

  const jobTitles = new Set(jobs.map(j => j.title));

  const applications: Application[] = apiCandidates.map((candidate) => ({
    id: candidate.documentId || candidate.id,
    jobId: candidate.appliedPosition || '',
    candidateName: `${candidate.firstName} ${candidate.lastName}`,
    email: candidate.email,
    phone: candidate.phone,
    appliedDate: candidate.appliedDate || '',
    status: candidate.status === 'new' ? 'Pending' :
           candidate.status === 'under_review' ? 'Reviewed' :
           candidate.status === 'interview_scheduled' ? 'Interview' :
           candidate.status === 'hired' ? 'Hired' : 'Rejected',
    rawStatus: candidate.status,
    resume: candidate.resumeUrl || '',
    rating: [],
    totalRating: candidate.totalRating || 0,
    evaluatedBy: candidate.assignedManagerName,
    evaluatedAt: candidate.interviewDate,
    notes: candidate.notes,
    rawCandidate: candidate,
    documentId: candidate.documentId
  } as any)).filter((app: Application) => {
    // The Applications tab shows only applications against job postings.
    // Seafarer-form submissions (source 'website_form') belong to the
    // Candidates module; legacy rows fall back to matching a job title.
    if ((app.rawCandidate as any)?.source === 'website_form') return false;
    return jobTitles.size === 0 || jobTitles.has(app.jobId);
  });

  const deleteJob = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this job posting?')) {
      await deleteCareerMutation.execute(id);
    }
  };

  const deleteApplication = async (application: Application) => {
    if (window.confirm(`Are you sure you want to delete the application from "${application.candidateName}"? This action cannot be undone.`)) {
      await deleteCandidateMutation.execute(application);
    }
  };

  const saveJob = async (jobData: Omit<Job, 'id' | 'applicants'>, editingJob: Job | null) => {
    if (editingJob) {
      await updateCareerMutation.execute({
        id: editingJob.id,
        data: {
          title: jobData.title,
          department: jobData.department,
          location: jobData.location,
          employmentType: convertEmploymentTypeToAPI(jobData.type),
          // salaryRange is a JSON column; the salary text must be sent as a
          // JSON-encoded string or Postgres rejects the write with a 500
          salaryRange: JSON.stringify(jobData.salary),
          applicationDeadline: jobData.deadline,
          isActive: jobData.status === 'Open',
          useSeafarerForm: !!jobData.useSeafarerForm,
          description: jobData.description,
          requirements: JSON.stringify(jobData.requirements)
        }
      });
    } else {
      await createCareerMutation.execute({
        title: jobData.title,
        arabicTitle: jobData.title,
        department: jobData.department,
        location: jobData.location,
        employmentType: convertEmploymentTypeToAPI(jobData.type),
        experienceLevel: 'mid_level',
        salaryRange: JSON.stringify(jobData.salary),
        applicationDeadline: jobData.deadline,
        isActive: jobData.status === 'Open',
        useSeafarerForm: !!jobData.useSeafarerForm,
        description: jobData.description,
        requirements: JSON.stringify(jobData.requirements),
        contactEmail: 'hr@petromarine.com',
        postedBy: 'Admin'
      });
    }
  };

  const calculateTotalRating = (ratings: CandidateRating[]): number => {
    if (ratings.length === 0) return 0;
    const totalWeightedScore = ratings.reduce((sum, rating) => {
      const point = assessmentPoints.find(p => p.id === rating.pointId!);
      return sum + (rating.score * (point?.weight || 1));
    }, 0);
    const totalWeight = ratings.reduce((sum, rating) => {
      const point = assessmentPoints.find(p => p.id === rating.pointId!);
      return sum + (point?.weight || 1);
    }, 0);
    return Math.round((totalWeightedScore / totalWeight) * 10) / 10;
  };

  const saveRating = async (ratings: CandidateRating[], applicationId: string) => {
    const totalRating = calculateTotalRating(ratings);
    await updateCandidateMutation.execute({
      id: applicationId,
      data: { totalRating, interviewDate: new Date().toISOString() }
    });
  };

  const submitCandidate = async (
    data: any,
    editingApplication: Application | null,
    selectedJobForApply: Job | null
  ) => {
    // CandidateForm collects cvFile (resume) and pdfFile (application PDF) —
    // upload them and link via resumeUrl / applicationPdfUrl.
    const fileFields: { resumeUrl?: string; applicationPdfUrl?: string } = {};
    if (data.cvFile instanceof File) {
      fileFields.resumeUrl = (await uploadFiles([data.cvFile]))[0]?.url;
    }
    if (data.pdfFile instanceof File) {
      fileFields.applicationPdfUrl = (await uploadFiles([data.pdfFile]))[0]?.url;
    }
    if (editingApplication) {
      await updateCandidateMutation.execute({
        id: editingApplication.id,
        data: {
          firstName: data.firstName,
          lastName: data.lastName,
          email: data.email || '',
          phone: data.phone || '',
          nationality: data.nationality || '',
          dateOfBirth: data.dateOfBirth || '',
          appliedPosition: data.appliedPosition || editingApplication.jobId,
          source: data.source || editingApplication.rawCandidate?.source || 'website',
          experience: data.experience ?? 0,
          education: data.education || '',
          certificates: data.certificates || [],
          languages: data.languages || [],
          previousCompany: data.previousCompany || '',
          expectedSalary: data.expectedSalary ?? 0,
          availableFrom: data.availableFrom || '',
          coverLetter: data.coverLetter || '',
          status: data.status,
          notes: data.notes || '',
          totalRating: data.totalRating || 0,
          ...fileFields,
        }
      });
    } else if (selectedJobForApply) {
      await createCandidateMutation.execute({
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email || '',
        phone: data.phone || '',
        appliedDate: new Date().toISOString().split('T')[0],
        appliedPosition: selectedJobForApply.title,
        status: 'new',
        nationality: data.nationality || '',
        dateOfBirth: data.dateOfBirth || '',
        source: data.source || 'website',
        experience: data.experience || 0,
        education: data.education || '',
        certificates: data.certificates || [],
        languages: data.languages || [],
        previousCompany: data.previousCompany || '',
        expectedSalary: data.expectedSalary || 0,
        availableFrom: data.availableFrom || '',
        coverLetter: data.coverLetter || '',
        assignedManagerId: '',
        assignedManagerName: '',
        managerFeedback: '',
        notes: data.notes || '',
        rating: [],
        totalRating: 0,
        ...fileFields,
      });
    }
  };

  const hiringAssessmentPoints = assessmentPoints.filter(point =>
    point.isActive && (point.applicableFor === 'hiring' || point.applicableFor === 'both')
  );

  return {
    jobs,
    applications,
    assessmentPoints: hiringAssessmentPoints,
    deleteJob,
    deleteApplication,
    saveJob,
    saveRating,
    submitCandidate,
  };
}
